import { Grid } from "@mui/material";
import { InfoBox } from "components/InfoBox";
import { WasteStream } from "generated-graphql/graphql";
import { useMemo } from "react";
import { formatAmountInPounds } from "util/formatNumber";

import { prettyPrintWasteClassification } from "../../prettyPrintWasteClassification";

interface MergedWasteStreamInfoProps {
  wasteStreams: WasteStream[];
  dateRangeText: string;
  isEditMode?: boolean;
}

export const MergedWasteStreamInfo = ({
  wasteStreams,
  dateRangeText,
  isEditMode = false,
}: MergedWasteStreamInfoProps) => {
  // Helper functions to get combined data from all selected rows
  const getCombinedData = useMemo(() => {
    // Get all unique profile numbers from both vendor and e‑Manifest
    const profileNumbers = wasteStreams
      .map((wasteStream) => {
        const eManifest = wasteStream.eManifestProfileNumber;
        const vendor = wasteStream.vendorProfileNumber;
        if (eManifest && vendor && vendor !== eManifest) {
          return `Vendor: ${vendor}, e‑Manifest: ${eManifest}`;
        }
        return eManifest || vendor || "";
      })
      .filter(Boolean);

    // Get all unique federal waste codes combined
    const federalWasteCodes = [
      ...new Set(wasteStreams.flatMap((wasteStream) => wasteStream.federalWasteCodes || [])),
    ];

    // Get all unique state waste codes combined
    const stateWasteCodes = [
      ...new Set(wasteStreams.flatMap((wasteStream) => wasteStream.stateWasteCodes || [])),
    ];

    // We can assume all selected rows have the same classification
    const classification = wasteStreams[0]?.classification;

    // Get all unique form codes
    const formCodes = [
      ...new Set(wasteStreams.map((wasteStream) => wasteStream.formCode).filter(Boolean)),
    ];

    // Get all unique source codes
    const sourceCodes = [
      ...new Set(wasteStreams.map((wasteStream) => wasteStream.sourceCode).filter(Boolean)),
    ];

    // Sum amounts for date range
    const dateRangeAmount = wasteStreams.reduce(
      (sum, wasteStream) => sum + (wasteStream.vendorAmount || wasteStream.eManifestAmount || 0),
      0
    );

    // Sum amounts for all time
    const allTimeAmount = wasteStreams.reduce(
      (sum, wasteStream) =>
        sum + (wasteStream.vendorAmountAllTime || wasteStream.eManifestAmountAllTime || 0),
      0
    );

    // Combine all comments
    const comments = wasteStreams
      .map((wasteStream) => wasteStream.comment?.trim())
      .filter(Boolean)
      .join("; ");

    return {
      profileNumbers: profileNumbers.join(", "),
      federalWasteCodes: federalWasteCodes.join(", "),
      stateWasteCodes: stateWasteCodes.join(", "),
      classification: classification,
      formCodes: formCodes.join(", "),
      sourceCodes: sourceCodes.join(", "),
      dateRangeAmount: formatAmountInPounds(dateRangeAmount),
      allTimeAmount: formatAmountInPounds(allTimeAmount),
      comments,
    };
  }, [wasteStreams]);

  return (
    <Grid container spacing={3}>
      {/* First row - read-only combined data */}
      <Grid size={{ xs: 12, md: 4 }}>
        <InfoBox label="Profile #" text={getCombinedData.profileNumbers} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <InfoBox label="Federal Waste Code" text={getCombinedData.federalWasteCodes} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <InfoBox label="State Waste Code" text={getCombinedData.stateWasteCodes} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <InfoBox
          label="Classification"
          text={prettyPrintWasteClassification(getCombinedData.classification)}
        />
      </Grid>

      {/* Second row - more read-only data */}
      <Grid size={{ xs: 12, md: 4 }}>
        <InfoBox label="Form Code" text={getCombinedData.formCodes} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <InfoBox label="Source Code" text={getCombinedData.sourceCodes} />
      </Grid>

      {/* Third row - amounts */}
      <Grid size={{ xs: 12, md: 6 }}>
        <InfoBox
          label={`Aggregated Amount [${dateRangeText}]`}
          text={getCombinedData.dateRangeAmount}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <InfoBox label="Aggregated Amount [All time]" text={getCombinedData.allTimeAmount} />
      </Grid>

      {/* Fourth row - comments */}
      {!isEditMode && (
        <Grid size={{ xs: 12 }}>
          <InfoBox label="Comment" text={getCombinedData.comments} />
        </Grid>
      )}
    </Grid>
  );
};
